import { WindowBreakpoints } from 'modules/core/enums/windowBreakpoints.enum';
import styled, { keyframes } from 'styled-components';
import { Tooltip } from 'react-tooltip';
import { Card, Col, Row } from 'react-bootstrap';
import { COLORS } from 'modules/core/theme/colors';
import { disabledSectionMixin } from 'modules/core/theme/mixins';

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const SearchFieldSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid ${COLORS.GREY_32};
  border-top-color: ${COLORS.GREY_24};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const HeaderButtonsGroup = styled.div<{ isRac: boolean }>`
  display: flex;
  justify-content: ${({ isRac }) => (isRac ? 'end' : 'space-between')};
  align-items: center;

  @media (max-width: ${WindowBreakpoints.small}px) {
    width: auto;
  }
`;

export const ConsumerInformationWrapper = styled.div`
  .card-title > span {
    margin-right: 5px;
    line-height: 2;
    gap: 16px;

    @media (max-width: ${WindowBreakpoints.medium}px) {
      margin-right: 0;
    }
  }

  .route-one-search-data input:disabled {
    color: ${COLORS.GREY_24};
    background-color: ${COLORS.GREY_31};
    font-weight: normal;
    border: 1px solid ${COLORS.GREY_32};
  }
`;

export const RouteOneAppTooltip = styled(Tooltip)`
  z-index: 998;
  max-width: 400px;
  min-width: 280px;
  opacity: 1 !important;
  background-color: ${COLORS.WHITE} !important;
  color: ${COLORS.BLACK} !important;
  left: 11px !important;
  top: 31px !important;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.25);
  font-weight: 400;

  .react-tooltip-arrow {
    left: 26px !important;
    top: -4px !important;
  }

  @media (max-width: ${WindowBreakpoints.medium}px) {
    max-width: calc(100vw - 40px);
    min-width: auto;
  }
`;

export const RouteOneErrorTooltip = styled(Tooltip)`
  z-index: 999;
  max-width: 400px;
  min-width: 280px;
  opacity: 1 !important;
  background-color: ${COLORS.RED_8} !important;
  color: ${COLORS.WHITE} !important;
  left: 11px !important;
  top: 31px !important;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.25);
  font-weight: 400;
  font-size: 12px;
  font-style: italic;

  .react-tooltip-arrow {
    left: 26px !important;
    top: -4px !important;
    border-bottom-color: ${COLORS.RED_8} !important;
  }

  @media (max-width: ${WindowBreakpoints.medium}px) {
    max-width: calc(100vw - 40px);
    min-width: auto;
  }
`;

export const ButtonIcon = styled.img<{ iconUrl: string; disabled?: boolean; marginRightSpace?: boolean }>`
  content: url('${({ iconUrl }) => iconUrl}');
  border: none;
  cursor: pointer;
  height: 100%;
  padding: 2px 0;

  ${({ marginRightSpace }) => marginRightSpace && 'margin-right: 10px'};
  ${({ disabled }) => disabled && disabledSectionMixin}

  @media (max-width: ${WindowBreakpoints.small}px) {
    display: none;
  }
`;

export const IncomeCalculatorIcon = styled.img<{ iconUrl: string; disabled?: boolean; marginRightSpace?: boolean }>`
  content: url('${({ iconUrl }) => iconUrl}');
  border: none;
  cursor: pointer;
  height: 30px;
  padding: 2px 0;
  margin-bottom: -3px;

  ${({ marginRightSpace }) => marginRightSpace && 'margin-right: 10px'};
  ${({ disabled }) => disabled && disabledSectionMixin}

  @media (max-width: ${WindowBreakpoints.small}px) {
    display: none;
  }
`;

export const HeaderFieldsContainer = styled(Row)`
  flex: 1;
  --bs-gutter-x: 12px;

  .dropdown {
    width: 100%;

    button {
      display: flex;
      align-items: center;
    }
  }

  @media (max-width: ${WindowBreakpoints.medium}px) {
    margin-top: 0;
    width: 100%;
    --bs-gutter-x: 0;
  }

  @media (min-width: ${WindowBreakpoints.medium + 1}px) and (max-width: ${WindowBreakpoints.large}px) {
    margin-top: 0;
    width: 100%;
  }
`;

export const FieldCol = styled(Col)`
  @media (max-width: ${WindowBreakpoints.medium}px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

export const RouteOneInputWrapper = styled.div<{ $hasError?: boolean }>`
  .input-group {
    background-color: white !important;
    border: 0.5px solid ${({ $hasError }) => ($hasError ? COLORS.RED_8 : '#747474')} !important;
    border-radius: 5px !important;

    input {
      background-color: transparent !important;
      color: #6b6b6b !important;
      font-weight: 400 !important;
      font-size: 12px !important;
    }

    .input-group-text {
      background-color: transparent !important;
    }

    input::placeholder {
      color: ${COLORS.GREY_24} !important;
    }
  }

  .input-group:has(input:disabled) {
    background-color: ${COLORS.GREY_31} !important;
    border: 1px solid ${COLORS.GREY_32} !important;
  }

  .input-group:has(input:disabled) input {
    color: ${COLORS.GREY_24} !important;
    background-color: ${COLORS.GREY_31} !important;
    font-weight: normal !important;
  }

  .input-group:has(input:disabled) .input-group-text {
    color: ${COLORS.GREY_24} !important;
    background-color: ${COLORS.GREY_31} !important;
    opacity: 0.5;
  }
`;

export const RouteOneDropdownWrapper = styled.div`
  .dropdown {
    button {
      background-color: white !important;
      border: 0.5px solid #747474 !important;
      border-radius: 5px !important;
      font-weight: 400 !important;
      font-size: 12px !important;
      color: #6b6b6b !important;
      display: block !important;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: left;

      &:disabled,
      &.disabled {
        color: ${COLORS.GREY_24} !important;
        background-color: ${COLORS.GREY_31} !important;
        border: 1px solid ${COLORS.GREY_32} !important;
        opacity: 1 !important;
        pointer-events: none;
      }
    }
  }
`;

export const PanelWithImageWrapper = styled.div`
  display: flex;
  position: relative;
  padding-left: 312px;
`;

export const SideImageContainer = styled(Card)`
  width: 100%;
  height: 100%;
  border-radius: 0.375rem;
  overflow: hidden;

  > img:first-child {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top left;
    padding: 0;
  }
`;

export const LinkOutIconWrapper = styled.div`
  position: absolute;
  top: 5px;
  right: 15px;
  width: 25px;
  height: 25px;
  background-color: rgba(255, 255, 255, 0.75);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  pointer-events: none;

  ${SideImageContainer}:hover & {
    opacity: 1;
  }
`;

export const LinkOutIcon = styled.img`
  width: 15px;
  height: 15px;
`;

export const OverrideFieldsWrapper = styled.div`
  position: relative;
  height: 100%;

  @media (min-width: ${WindowBreakpoints.medium + 1}px) {
    padding-left: 12px;
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 1px;
      background: ${COLORS.WHITE_2};
    }
  }

  .card {
    border: none;
    background: transparent;
    box-shadow: none;
  }

  .card-title {
    min-height: auto !important;
    padding: 0 0 4px !important;
    font-size: 14px !important;
    font-weight: 700 !important;
    color: ${COLORS.GREY_32} !important;

    span {
      line-height: normal;
    }
  }

  .card > div:not(.card-title):not(.card-body) {
    display: none;
  }

  .card-body {
    padding: 0 !important;
  }

  .input-group {
    background-color: ${COLORS.WHITE} !important;
    border: 0.5px solid ${COLORS.GREY_3} !important;
    border-radius: 5px !important;

    input {
      background-color: transparent !important;
      color: ${COLORS.GREY_40} !important;
      font-weight: 400 !important;
      font-size: 12px !important;
    }

    .input-group-text {
      background-color: transparent !important;
      color: ${COLORS.GREY_40} !important;
    }

    input::placeholder {
      color: ${COLORS.GREY_40} !important;
    }
  }

  .input-group:has(input:disabled) {
    background-color: ${COLORS.GREY_31} !important;
    border: 1px solid ${COLORS.GREY_32} !important;
    box-shadow: none !important;
  }

  .input-group:has(input:disabled) input {
    color: ${COLORS.GREY_24} !important;
    background-color: ${COLORS.GREY_31} !important;
    font-weight: normal !important;
  }

  .input-group:has(input:disabled) .input-group-text {
    color: ${COLORS.GREY_24} !important;
    background-color: ${COLORS.GREY_31} !important;
  }

  @media (max-width: ${WindowBreakpoints.medium}px) {
    margin-top: 15px;
  }
`;
